import { zodResolver } from "@hookform/resolvers/zod"
import { Pencil, Save, X } from "lucide-react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form"
import { cn } from "@/lib/utils"
import type { IntakeLoad, LabResults } from "@/features/intake/api/types"
import { DecimalInput } from "@/features/intake/components/decimal-input"
import { LabFieldHint } from "@/features/intake/components/lab-field-hint"
import { LiveGradePanel } from "@/features/intake/components/live-grade-panel"
import { SectionCard } from "@/features/intake/components/section-card"
import { useSaveLab } from "@/features/intake/hooks/use-intake-mutations"
import { WET_BAG_KG } from "@/features/intake/lib/load-formulas"
import { defectueuseSum } from "@/features/intake/lib/quality-rules"
import { formatNumber } from "@/lib/format"

const percentage = z
  .union([z.string(), z.number()])
  .transform((v) => Number(v))
  .refine((n) => !Number.isNaN(n) && n >= 0)

const labSchema = z.object({
  grainage: percentage,
  foreignMatter: percentage,
  moisture: percentage,
  broken: percentage,
  waste: percentage,
  crabot: percentage,
  mouldy: percentage,
  slaty: percentage,
  germinated: percentage,
  mites: percentage,
  flatBeans: percentage,
  violette: percentage,
})

type LabFormInput = z.input<typeof labSchema>
type LabFormOutput = z.output<typeof labSchema>

const REFRACTION_FIELDS = [
  "moisture",
  "foreignMatter",
  "broken",
  "waste",
  "crabot",
] as const

const CLASSIFICATION_FIELDS = [
  "grainage",
  "mouldy",
  "slaty",
  "germinated",
  "mites",
  "flatBeans",
  "violette",
] as const

interface LabCardProps {
  load: IntakeLoad
  isEditing: boolean
  onEditingChange: (editing: boolean) => void
  // When true, the card stays in saved/display mode and the Edit
  // affordance is hidden. Used when the load is COMPLETED or when the
  // user is inspecting this stage from the stepper.
  readOnly?: boolean
}

function asInputNumber(v: number | null | undefined): number {
  return (v ?? "") as unknown as number
}

function buildDefaults(lab: LabResults | null | undefined): LabFormInput {
  return {
    grainage: asInputNumber(lab?.grainage),
    foreignMatter: asInputNumber(lab?.foreignMatter),
    moisture: asInputNumber(lab?.moisture),
    broken: asInputNumber(lab?.broken),
    waste: asInputNumber(lab?.waste),
    crabot: asInputNumber(lab?.crabot),
    mouldy: asInputNumber(lab?.mouldy),
    slaty: asInputNumber(lab?.slaty),
    germinated: asInputNumber(lab?.germinated),
    mites: asInputNumber(lab?.mites),
    flatBeans: asInputNumber(lab?.flatBeans),
    violette: asInputNumber(lab?.violette),
  }
}

export function LabCard({
  load,
  isEditing,
  onEditingChange,
  readOnly = false,
}: LabCardProps) {
  const { t } = useTranslation(["intake", "common"])
  const saveLab = useSaveLab(load.id)
  const lab = load.labResults
  const saved = !!lab
  const showForm = !readOnly && (!saved || isEditing)

  const form = useForm<LabFormInput, unknown, LabFormOutput>({
    resolver: zodResolver(labSchema),
    defaultValues: buildDefaults(lab),
  })

  useEffect(() => {
    if (showForm) {
      form.reset(buildDefaults(lab))
    }
  }, [showForm, lab, form])

  const v = form.watch()
  const allFieldsFilled =
    REFRACTION_FIELDS.every((f) => v[f] !== "" && v[f] !== undefined) &&
    CLASSIFICATION_FIELDS.every((f) => v[f] !== "" && v[f] !== undefined)

  const onSubmit = form.handleSubmit(async (parsed) => {
    await saveLab.mutateAsync({
      grainage: parsed.grainage,
      foreignMatter: parsed.foreignMatter,
      moisture: parsed.moisture,
      broken: parsed.broken,
      waste: parsed.waste,
      crabot: parsed.crabot,
      mouldy: parsed.mouldy,
      slaty: parsed.slaty,
      germinated: parsed.germinated,
      mites: parsed.mites,
      flatBeans: parsed.flatBeans,
      violette: parsed.violette,
    })
    onEditingChange(false)
  })

  const handleCancel = () => {
    form.reset(buildDefaults(lab))
    onEditingChange(false)
  }

  // Accepted weight = max(0, netWeight − bagsWet × 65). Drives kg
  // refraction in the live preview. When sampling hasn't been saved yet
  // we treat bagsWet as 0 — the BE recomputes refraction on every
  // sampling save once the lab row is filled in.
  const netWeight = load.netWeight ?? 0
  const bagsWet = load.sampling?.bagsWet ?? 0
  const acceptedKg = Math.max(0, netWeight - bagsWet * WET_BAG_KG)

  // For the LiveGradePanel: when editing or first-time, use form watches.
  // When saved (read-only), use persisted values so the live panel mirrors
  // the stamped grade exactly.
  const refractionInputs = showForm
    ? {
        moisture: Number(v.moisture) || 0,
        foreignMatter: Number(v.foreignMatter) || 0,
        broken: Number(v.broken) || 0,
        waste: Number(v.waste) || 0,
        crabot: Number(v.crabot) || 0,
        mouldy: Number(v.mouldy) || 0,
      }
    : {
        moisture: lab?.moisture ?? 0,
        foreignMatter: lab?.foreignMatter ?? 0,
        broken: lab?.broken ?? 0,
        waste: lab?.waste ?? 0,
        crabot: lab?.crabot ?? 0,
        mouldy: lab?.mouldy ?? 0,
      }
  const classificationInputs = showForm
    ? {
        mouldy: Number(v.mouldy) || 0,
        slaty: Number(v.slaty) || 0,
        germinated: Number(v.germinated) || 0,
        mites: Number(v.mites) || 0,
        flatBeans: Number(v.flatBeans) || 0,
      }
    : {
        mouldy: lab?.mouldy ?? 0,
        slaty: lab?.slaty ?? 0,
        germinated: lab?.germinated ?? 0,
        mites: lab?.mites ?? 0,
        flatBeans: lab?.flatBeans ?? 0,
      }
  const liveReady = showForm ? allFieldsFilled : saved

  const labelClass = "text-[10px] font-medium uppercase tracking-wide"
  const sectionLabelClass =
    "text-muted-foreground text-[10px] font-semibold uppercase tracking-wide"

  return (
    <Form {...form}>
      <form onSubmit={onSubmit}>
        <SectionCard
          kicker={t("lab.kicker")}
          title={t("lab.title")}
          subtitle={t("lab.subtitle")}
          roleBadge={t("detail.role.lab")}
          state={lab ? "saved" : "default"}
        >
          <div className="grid gap-5 lg:grid-cols-[1fr_minmax(320px,360px)]">
            <div className="space-y-4">
              <div className="space-y-2">
                <div className={sectionLabelClass}>
                  {t("lab.refractionHeader")}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
                  {REFRACTION_FIELDS.map((name) =>
                    showForm ? (
                      <FormField
                        key={name}
                        control={form.control}
                        name={name}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel
                              className={cn(
                                labelClass,
                                "flex items-center justify-between gap-1"
                              )}
                            >
                              <span className="truncate">
                                {t(`lab.fields.${name}`)} %
                              </span>
                              <LabFieldHint fieldKey={name} />
                            </FormLabel>
                            <FormControl>
                              <DecimalInput
                                placeholder="0"
                                className="h-9"
                                {...field}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    ) : (
                      <ReadOnlyLabField
                        key={name}
                        label={`${t(`lab.fields.${name}`)} %`}
                        fieldKey={name}
                        value={lab?.[name]}
                        suffix="%"
                      />
                    ),
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className={sectionLabelClass}>
                  {t("lab.classificationHeader")}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
                  {CLASSIFICATION_FIELDS.map((name) =>
                    showForm ? (
                      <FormField
                        key={name}
                        control={form.control}
                        name={name}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel
                              className={cn(
                                labelClass,
                                "flex items-center justify-between gap-1"
                              )}
                            >
                              <span className="truncate">
                                {name === "grainage"
                                  ? t(`lab.fields.${name}`)
                                  : `${t(`lab.fields.${name}`)} %`}
                              </span>
                              <LabFieldHint fieldKey={name} />
                            </FormLabel>
                            <FormControl>
                              <DecimalInput
                                placeholder="0"
                                className="h-9"
                                {...field}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    ) : (
                      <ReadOnlyLabField
                        key={name}
                        label={
                          name === "grainage"
                            ? t(`lab.fields.${name}`)
                            : `${t(`lab.fields.${name}`)} %`
                        }
                        fieldKey={name}
                        value={lab?.[name]}
                        suffix={name === "grainage" ? "" : "%"}
                      />
                    ),
                  )}
                </div>
                <DefectueuseHint
                  germinated={classificationInputs.germinated}
                  mites={classificationInputs.mites}
                  flatBeans={classificationInputs.flatBeans}
                />
              </div>
            </div>

            <LiveGradePanel
              refraction={refractionInputs}
              classification={classificationInputs}
              acceptedKg={acceptedKg}
              ready={liveReady}
              stamped={!showForm && saved}
              stampedGrade={!showForm && saved ? load.grade ?? null : null}
            />
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            {showForm ? (
              <p className="text-xs text-muted-foreground">
                {allFieldsFilled ? t("lab.hintReady") : t("lab.hintFill")}
              </p>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              {showForm ? (
                <>
                  {saved ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      disabled={saveLab.isPending}
                    >
                      <X className="size-4" />
                      {t("common:actions.cancel")}
                    </Button>
                  ) : null}
                  <Button
                    type="submit"
                    disabled={!allFieldsFilled || saveLab.isPending}
                  >
                    <Save className="size-4" />
                    {saved ? t("lab.updateCta") : t("lab.saveCta")}
                  </Button>
                </>
              ) : readOnly ? null : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onEditingChange(true)}
                >
                  <Pencil className="size-4" />
                  {t("lab.editCta")}
                </Button>
              )}
            </div>
          </div>
        </SectionCard>
      </form>
    </Form>
  )
}

interface ReadOnlyLabFieldProps {
  label: string
  fieldKey: string
  value: number | null | undefined
  suffix: string
}

function ReadOnlyLabField({
  label,
  fieldKey,
  value,
  suffix,
}: ReadOnlyLabFieldProps) {
  const display = value == null ? "—" : formatNumber(value, 2)
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-1 text-[10px] font-medium uppercase tracking-wide">
        <span className="truncate">{label}</span>
        <LabFieldHint fieldKey={fieldKey} />
      </div>
      <div className="bg-muted/40 flex h-9 items-center justify-between rounded-md border px-3 text-sm tabular-nums">
        <span className="font-medium">{display}</span>
        {suffix ? (
          <span className="text-muted-foreground text-xs">{suffix}</span>
        ) : null}
      </div>
    </div>
  )
}

interface DefectueuseHintProps {
  germinated: number
  mites: number
  flatBeans: number
}

// DefectueuseHint surfaces the running défectueuse composite
// (germinated + mites + flat beans) under the classification grid so
// the analyst can see the value driving the grade without doing the
// addition in their head. Mirrors the same composite used in
// classifyGrade and the QualityMath dialog.
function DefectueuseHint({
  germinated,
  mites,
  flatBeans,
}: DefectueuseHintProps) {
  const { t } = useTranslation("intake")
  const total = defectueuseSum({
    germinated,
    mites,
    flatBeans,
    // The other ClassificationInputs fields are unused by defectueuseSum
    // but the type requires them.
    mouldy: 0,
    slaty: 0,
  })
  const fmt = (n: number) => formatNumber(n, 2)
  return (
    <div className="bg-muted/30 mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-md border px-2.5 py-1.5 text-[11px] tabular-nums">
      <span className="text-foreground font-semibold">
        {t("lab.fields.defectueuse")} ({fmt(total)} %)
      </span>
      <span className="text-muted-foreground">
        = {t("lab.fields.germinated")} {fmt(germinated)} % +{" "}
        {t("lab.fields.mites")} {fmt(mites)} % +{" "}
        {t("lab.fields.flatBeans")} {fmt(flatBeans)} %
      </span>
      <span className="ml-auto">
        <LabFieldHint fieldKey="defectueuse" />
      </span>
    </div>
  )
}
